import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../auth/AuthContext';
import { useMembership } from '../../auth/MembershipContext';
import Notification from '../../components/Notification/Notification';
import PageTemplate from '../../components/PageTemplate/PageTemplate';
import { P } from '../../components/ui';
import { SUPPRESS_GLOBAL_LOADING_META } from '../../context/UiContext';
import { useFormConfig } from '../../hooks/useFormConfig';
import {
  APPROVAL_GROUP_TENANT,
  type MemberInvite,
  type MemberPatch,
  type MembersResponse,
  canManageMembers,
  inviteMembers,
  listMembers,
  removeMember,
  updateMember,
} from '../../service/membership';
import { Query } from '../../types';
import { ApiError } from '../../utils/api-error';
import InviteMembersForm from './InviteMembersForm';
import MemberEditor from './MemberEditor';
import MemberRow from './MemberRow';

type Feedback = { type: 'success' | 'error'; message: string };

const describeError = (error: unknown, fallback: string): string => {
  if (error instanceof ApiError) return error.detail || error.message || fallback;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

/**
 * `/<tenant>/members`: the tenant's membership rows for its admins and for
 * super admins. The API enforces the same rule on every call; this screen
 * only decides what to show.
 */
export default function MembersScreen() {
  const id = useId();
  const { tenant, formConfig } = useFormConfig();
  const { fetchJWT, user } = useAuthContext();
  const { me, isLoading: isMembershipLoading } = useMembership();
  const queryClient = useQueryClient();
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [filter, setFilter] = useState('');
  const [editingEmail, setEditingEmail] = useState<string | null>(null);

  const tenantSlug = tenant || '';
  const serviceName = formConfig?.formName || tenantSlug || 'this service';
  const canManage = canManageMembers(me, tenantSlug);
  const showApprovalGroups = tenantSlug === APPROVAL_GROUP_TENANT;
  const queryKey = [Query.TenantMembers, tenantSlug];

  const token = async (): Promise<string> => {
    const { idToken } = await fetchJWT({ redirectOnFail: false });
    if (!idToken) throw new Error('Your session has expired. Sign in again.');
    return idToken;
  };

  const membersQuery = useQuery<MembersResponse>({
    queryKey,
    enabled: Boolean(tenantSlug) && canManage,
    queryFn: async () => listMembers(await token(), tenantSlug),
    staleTime: 0,
    retry: false,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey });
  const fail = (fallback: string) => (error: unknown) =>
    setFeedback({ type: 'error', message: describeError(error, fallback) });

  const invite = useMutation({
    meta: SUPPRESS_GLOBAL_LOADING_META,
    mutationFn: async (members: MemberInvite[]) => inviteMembers(await token(), tenantSlug, members),
    onSuccess: async (data) => {
      const count = data.members.length;
      setFeedback({
        type: 'success',
        message: `${count} ${count === 1 ? 'person' : 'people'} invited. They will see ${serviceName} the next time they sign in.`,
      });
      await refresh();
    },
    onError: fail('Error: failed to invite members, try again later'),
  });

  const update = useMutation({
    meta: SUPPRESS_GLOBAL_LOADING_META,
    mutationFn: async ({ email, patch }: { email: string; patch: MemberPatch }) =>
      updateMember(await token(), tenantSlug, email, patch),
    onSuccess: async (data) => {
      setFeedback({ type: 'success', message: `${data.member.email} updated` });
      setEditingEmail(null);
      await refresh();
    },
    onError: fail('Error: failed to update member, try again later'),
  });

  const remove = useMutation({
    meta: SUPPRESS_GLOBAL_LOADING_META,
    mutationFn: async (email: string) => {
      await removeMember(await token(), tenantSlug, email);
      return email;
    },
    onSuccess: async (email) => {
      setFeedback({ type: 'success', message: `${email} removed` });
      setEditingEmail(null);
      await refresh();
    },
    onError: fail('Error: failed to remove member, try again later'),
  });

  if (!canManage) {
    return (
      <PageTemplate title="Members">
        {me === undefined && isMembershipLoading ? (
          <P role="status" aria-live="polite">
            Checking your access…
          </P>
        ) : (
          <>
            <P>Only admins of {serviceName} can manage its members.</P>
            <P>
              If you think you should be an admin, ask an existing admin of this service to change
              your role.
            </P>
            <Link className="govuk-link" to={`/${tenantSlug}`}>
              Return to {serviceName}
            </Link>
          </>
        )}
      </PageTemplate>
    );
  }

  const members = [...(membersQuery.data?.members || [])].sort((a, b) =>
    a.email.localeCompare(b.email, 'en'),
  );
  const needle = filter.trim().toLowerCase();
  const visibleMembers = needle
    ? members.filter((member) => member.email.toLowerCase().includes(needle))
    : members;
  const approvalGroupOptions = membersQuery.data?.approvalGroupOptions || [];
  const busy = invite.isPending || update.isPending || remove.isPending;
  const ownEmail = String(user?.email || '').toLowerCase();
  const columnCount = showApprovalGroups ? 5 : 4;
  const countLabel =
    visibleMembers.length === members.length
      ? `${members.length} ${members.length === 1 ? 'member' : 'members'}`
      : `${visibleMembers.length} of ${members.length} members`;

  return (
    <PageTemplate title="Members" fullWidth>
      <P>
        People who can use {serviceName}. Admins can invite people, change roles
        {showApprovalGroups ? ' and approval groups,' : ''} and remove members.
      </P>

      {feedback && (
        <Notification
          header={feedback.type === 'success' ? 'Success' : 'Error'}
          content={feedback.message}
          className={
            feedback.type === 'success'
              ? 'govuk-notification-banner--success'
              : 'govuk-notification-banner--error'
          }
        />
      )}

      <details className="govuk-details">
        <summary className="govuk-details__summary">
          <span className="govuk-details__summary-text">Invite people</span>
        </summary>
        <div className="govuk-details__text">
          <InviteMembersForm
            busy={invite.isPending}
            onInvite={async (newMembers) => {
              setFeedback(null);
              await invite.mutateAsync(newMembers);
            }}
          />
        </div>
      </details>

      {membersQuery.isLoading && (
        <P role="status" aria-live="polite">
          Loading members…
        </P>
      )}
      {membersQuery.isError && (
        <Notification
          header="Error"
          content="Unable to load members right now."
          className="govuk-notification-banner--error"
        />
      )}

      {membersQuery.data && (
        <>
          <div className="govuk-form-group">
            <label className="govuk-label" htmlFor={`${id}-filter`}>
              Filter by email
            </label>
            <input
              className="govuk-input govuk-input--width-20"
              id={`${id}-filter`}
              type="search"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
          </div>

          <table className="govuk-table">
            <caption className="govuk-table__caption govuk-table__caption--m">{countLabel}</caption>
            <thead className="govuk-table__head">
              <tr className="govuk-table__row">
                <th scope="col" className="govuk-table__header">
                  Email
                </th>
                <th scope="col" className="govuk-table__header">
                  Status
                </th>
                <th scope="col" className="govuk-table__header">
                  Role
                </th>
                {showApprovalGroups && (
                  <th scope="col" className="govuk-table__header">
                    Approval groups
                  </th>
                )}
                <th scope="col" className="govuk-table__header">
                  <span className="govuk-visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="govuk-table__body">
              {visibleMembers.length === 0 && (
                <tr className="govuk-table__row">
                  <td className="govuk-table__cell" colSpan={columnCount}>
                    No members match that filter.
                  </td>
                </tr>
              )}
              {visibleMembers.map((member) => {
                const isSelf = member.email.toLowerCase() === ownEmail;
                const isEditing = editingEmail === member.email;
                return [
                  <MemberRow
                    key={member.email}
                    member={member}
                    isSelf={isSelf}
                    showApprovalGroups={showApprovalGroups}
                    isEditing={isEditing}
                    disabled={busy}
                    onEdit={() => {
                      setFeedback(null);
                      setEditingEmail(isEditing ? null : member.email);
                    }}
                  />,
                  isEditing ? (
                    <MemberEditor
                      key={`${member.email}-editor`}
                      member={member}
                      isSelf={isSelf}
                      showApprovalGroups={showApprovalGroups}
                      approvalGroupOptions={approvalGroupOptions}
                      columnCount={columnCount}
                      busy={busy}
                      onSave={(patch) => update.mutate({ email: member.email, patch })}
                      onRemove={() => remove.mutate(member.email)}
                      onCancel={() => setEditingEmail(null)}
                    />
                  ) : null,
                ];
              })}
            </tbody>
          </table>
        </>
      )}

      <Link className="govuk-link" to={`/${tenantSlug}`}>
        Return to {serviceName}
      </Link>
    </PageTemplate>
  );
}
